import React, { useEffect, useState, Component } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { FilterBar } from './FilterBar';
import { ModelsPanel } from './ModelsPanel';
import { ChartsPanel } from './ChartsPanel';
import { MarketZonesPanel } from './MarketZonesPanel';
import { HeatmapPanel } from './HeatmapPanel';
import { CardCustomizationPanel, DashboardCard } from './CardCustomizationPanel';
import { OverviewView } from './views/OverviewView';
import { ForecastView } from './views/ForecastView';
import { BenchmarksView } from './views/BenchmarksView';
import { OracleView } from './views/OracleView';
import { TradingStrategiesView } from './views/TradingStrategiesView';
import { DashboardsView } from './views/DashboardsView';
import { EuropePricingDashboard } from './views/EuropePricingDashboard';
import { GermanyPricingDashboard } from './views/GermanyPricingDashboard';
import { NordicsWeatherDashboard } from './views/NordicsWeatherDashboard';
import { SpainSolarDashboard } from './views/SpainSolarDashboard';
import { UKTradingDashboard } from './views/UKTradingDashboard';
import { FranceMarketDashboard } from './views/FranceMarketDashboard';
import { CrossBorderFlowDashboard } from './views/CrossBorderFlowDashboard';
import { RenewableCorrelationDashboard } from './views/RenewableCorrelationDashboard';
import { IntradayLiquidityDashboard } from './views/IntradayLiquidityDashboard';
import { PositionRiskDashboard } from './views/PositionRiskDashboard';
import { WeatherImpactDashboard } from './views/WeatherImpactDashboard';
import { BalancingMarketDashboard } from './views/BalancingMarketDashboard';
import { AssetPerformanceDashboard } from './views/AssetPerformanceDashboard';
import { CongestionAnalysisDashboard } from './views/CongestionAnalysisDashboard';
import { MLModelPerformanceDashboard } from './views/MLModelPerformanceDashboard';
import { ScenarioPlanningDashboard } from './views/ScenarioPlanningDashboard';
import { BriefingView } from './views/BriefingView';
import { useDashboardState } from '../hooks/useDashboardState';
import { useChatGPT } from '../hooks/useChatGPT';
import { OracleChatWidget } from './OracleChatWidget';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, Map, TrendingUp, Grid3x3, Menu } from 'lucide-react';
import { loadMarketDataFromCSV, getAvailableRegions } from '../services/csvParser';
import { matchDashboardTemplate, generateDashboardId, DashboardConfig } from '../utils/dashboardTemplates';
const initialCards: DashboardCard[] = [{
  id: 'models',
  name: 'Models',
  icon: TrendingUp,
  visible: true,
  description: 'Model selection and run times',
  size: 'small'
}, {
  id: 'charts',
  name: 'Forecast Chart',
  icon: BarChart3,
  visible: true,
  description: 'Time series visualization',
  size: 'large'
}, {
  id: 'zones',
  name: 'Market Zones',
  icon: Map,
  visible: true,
  description: 'Geographic zone selection',
  size: 'small'
}, {
  id: 'heatmap',
  name: 'Heatmap Analysis',
  icon: Grid3x3,
  visible: true,
  description: 'Deviations and disagreements',
  size: 'large'
}];
export function Dashboard() {
  const {
    activeRegion,
    setActiveRegion,
    activeFilters,
    toggleFilter,
    searchQuery,
    setSearchQuery,
    isSidebarCollapsed,
    toggleSidebar,
    activeView,
    setActiveView,
    activeZone,
    setActiveZone,
    activeVariable,
    setActiveVariable
  } = useDashboardState();
  // Lift chat state to Dashboard level to preserve across navigation
  const {
    messages: chatMessages,
    sendMessage: sendChatMessage,
    isLoading: isChatLoading,
    suggestedFollowUps: chatFollowUps,
    clearHistory: clearChatHistory,
    loadMessages: loadChatMessages
  } = useChatGPT({
    activeRegion,
    initialContext: `You are assisting an energy trader analyzing weather patterns and their market impact.

DEFAULT CONTEXT: This week, Europe, volatility focus

When providing trading insights, ALWAYS include:
1. Weather pattern analysis
2. Market impact assessment
3. **FINANCIAL PRODUCT SUGGESTIONS** with this structure:
   - Timeframe: [specific period, e.g., "3-7 days", "next 48h"]
   - Weather Pattern: [specific pattern, e.g., "low wind risk", "high solar output"]
   - Strategy: [trading approach, e.g., "long baseload power", "short day-ahead"]
   - Product: [specific instruments, e.g., "DE Power Futures", "ES Day-ahead", "Nordic system price"]
   - Position: [long/short/hedge]
   - Rationale: [why this product fits the weather pattern]

PRODUCT CATEGORIES TO CONSIDER:
- Power futures (day-ahead, baseload, peak)
- Renewable certificates (wind, solar)
- Gas spreads and futures
- Carbon permits
- Interconnector capacity
- Storage rights
- Options and derivatives
- Weather derivatives (HDD/CDD swaps)

Keep responses:
- CONCISE (2-3 short paragraphs max)
- ACTIONABLE (focus on trading implications)
- QUANTIFIED (use specific numbers, ranges, probabilities)
- STRUCTURED (use bullet points for clarity)

Format: Brief summary → Key numbers → **Trade Suggestion** (with timeframe/pattern/strategy/product/position) → Risk factors`,
    useMockMode: false
  });
  const [isCardPanelOpen, setIsCardPanelOpen] = useState(false);
  const [cards, setCards] = useState<DashboardCard[]>(initialCards);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());
  const [availableRegions, setAvailableRegions] = useState<string[]>(['Germany', 'Spain', 'UK', 'France']);
  // Dashboard state for DashboardsView
  // Split into visible (shown in overview) and all available (for creation modal)
  const [visibleDashboards, setVisibleDashboards] = useState<Array<{
    id: string;
    name: string;
    description: string;
    lastModified: string;
    charts: number;
    config?: DashboardConfig;
  }>>([
  // Only Regional Dashboards (excluding Nordics)
  {
    id: 'germany-pricing',
    name: 'Germany Pricing Dashboard',
    description: 'Real-time power pricing, day-ahead markets, and trading opportunities in Germany',
    lastModified: new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    charts: 6
  }, {
    id: 'spain-solar',
    name: 'Spain Solar Generation Analysis',
    description: 'Solar irradiance forecasts, generation capacity, and market impact analysis for Spain',
    lastModified: new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    charts: 6
  }, {
    id: 'uk-trading',
    name: 'UK Trading Opportunities Dashboard',
    description: 'Trading signals, spread opportunities, and market volatility analysis for UK power markets',
    lastModified: new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    charts: 7
  }, {
    id: 'france-market',
    name: 'France Power Market Overview',
    description: 'Comprehensive market analysis including nuclear generation, pricing, and cross-border flows',
    lastModified: new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    charts: 6
  }]);
  // All available dashboards (for creation modal)
  const allAvailableDashboards = [
  // Regional Dashboards
  {
    id: 'germany-pricing',
    name: 'Germany Pricing Dashboard',
    description: 'Real-time power pricing, day-ahead markets, and trading opportunities in Germany',
    category: 'Regional'
  }, {
    id: 'nordics-weather',
    name: 'Nordics Weather & Wind Forecast',
    description: 'Comprehensive wind generation forecasts and weather patterns across Nordic countries',
    category: 'Regional'
  }, {
    id: 'spain-solar',
    name: 'Spain Solar Generation Analysis',
    description: 'Solar irradiance forecasts, generation capacity, and market impact analysis for Spain',
    category: 'Regional'
  }, {
    id: 'uk-trading',
    name: 'UK Trading Opportunities Dashboard',
    description: 'Trading signals, spread opportunities, and market volatility analysis for UK power markets',
    category: 'Regional'
  }, {
    id: 'france-market',
    name: 'France Power Market Overview',
    description: 'Comprehensive market analysis including nuclear generation, pricing, and cross-border flows',
    category: 'Regional'
  },
  // Market Intelligence Dashboards
  {
    id: 'cross-border-flow',
    name: 'Cross-Border Flow Analysis',
    description: 'Interconnector capacity, scheduled flows, congestion patterns, and arbitrage opportunities',
    category: 'Market Intelligence'
  }, {
    id: 'renewable-correlation',
    name: 'Renewable Correlation Matrix',
    description: 'Wind and solar correlation across regions for portfolio diversification analysis',
    category: 'Market Intelligence'
  }, {
    id: 'intraday-liquidity',
    name: 'Intraday Liquidity Monitor',
    description: 'Bid-ask spreads, order book depth, and trading volume analysis across markets',
    category: 'Market Intelligence'
  },
  // Risk & Portfolio Dashboards
  {
    id: 'position-risk',
    name: 'Position Risk Dashboard',
    description: 'Real-time P&L, VaR calculations, Greeks, and stress test scenarios',
    category: 'Risk & Portfolio'
  }, {
    id: 'weather-impact',
    name: 'Weather Impact Analyzer',
    description: 'Multi-provider weather forecasts and quantified impact on generation and demand',
    category: 'Risk & Portfolio'
  }, {
    id: 'balancing-market',
    name: 'Balancing Market Insights',
    description: 'System frequency, imbalance prices, reserve activations, and balancing cost predictions',
    category: 'Risk & Portfolio'
  },
  // Operational Dashboards
  {
    id: 'asset-performance',
    name: 'Asset Performance Tracker',
    description: 'Actual vs forecast generation, availability tracking, and revenue optimization',
    category: 'Operational'
  }, {
    id: 'congestion-analysis',
    name: 'Congestion & Constraint Analysis',
    description: 'Network bottlenecks, redispatch costs, and locational pricing patterns',
    category: 'Operational'
  },
  // Advanced Analytics Dashboards
  {
    id: 'ml-model-performance',
    name: 'ML Model Performance Dashboard',
    description: 'Forecast accuracy comparison, confidence intervals, model drift, and feature importance',
    category: 'Advanced Analytics'
  }, {
    id: 'scenario-planning',
    name: 'Scenario Planning Workspace',
    description: 'Interactive what-if analysis with variable adjustments and cascading impact visualization',
    category: 'Advanced Analytics'
  }];
  const handleSelectDashboard = (id: string) => {
    // Navigate to the selected dashboard - simply set the active view to the dashboard ID
    setActiveView(id);
  };
  const handleAddDashboard = (dashboardId: string) => {
    // Find the dashboard in allAvailableDashboards
    const dashboardToAdd = allAvailableDashboards.find(d => d.id === dashboardId);
    if (!dashboardToAdd) return;
    // Check if already visible
    if (visibleDashboards.find(d => d.id === dashboardId)) {
      // Already added - do nothing
      return;
    }
    // Add to visible dashboards (but don't navigate)
    const newDashboard = {
      id: dashboardToAdd.id,
      name: dashboardToAdd.name,
      description: dashboardToAdd.description,
      lastModified: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      charts: 6
    };
    setVisibleDashboards(prev => [...prev, newDashboard]);
  };
  const handleRemoveDashboard = (dashboardId: string) => {
    // Remove dashboard from visible list
    setVisibleDashboards(prev => prev.filter(d => d.id !== dashboardId));
  };
  const handleCreateDashboard = async (prompt: string) => {
    // Match prompt to template
    const config = matchDashboardTemplate(prompt);
    if (!config) {
      console.error('Could not match prompt to template');
      return;
    }
    // Generate dashboard ID
    const dashboardId = generateDashboardId(config);
    // Create new dashboard entry
    const newDashboard = {
      id: dashboardId,
      name: config.customization.title,
      description: config.customization.description,
      lastModified: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      charts: 6,
      config
    };
    // Add to dashboards list (but don't navigate)
    setVisibleDashboards(prev => [...prev, newDashboard]);
    // Removed: setActiveView(dashboardId) - let user close modal first
  };
  // Remove CSV loading - just use the default regions
  // No useEffect needed
  const visibleCards = cards.filter(c => c.visible);
  const visibleCount = visibleCards.length;
  // Update unread status when notifications change
  useEffect(() => {
    const unreadCount = notifications.filter(n => !readNotifications.has(n.id)).length;
    setHasUnreadNotifications(unreadCount > 0);
  }, [notifications, readNotifications]);
  const markAllNotificationsAsRead = () => {
    const allIds = new Set(notifications.map(n => n.id));
    setReadNotifications(allIds);
  };
  const markNotificationAsRead = (notificationId: string) => {
    setReadNotifications(prev => new Set([...prev, notificationId]));
  };
  // Listen for navigation events from child components
  useEffect(() => {
    const handleNavigate = (e: CustomEvent) => {
      if (e.detail.view) {
        setActiveView(e.detail.view);
      }
    };
    // FIXED: Renamed to avoid naming conflict with the actual handleAddDashboard function
    const handleAddDashboardEvent = (e: CustomEvent) => {
      if (e.detail.dashboardId) {
        handleAddDashboard(e.detail.dashboardId);
      }
    };
    window.addEventListener('navigate-to-view', handleNavigate as EventListener);
    window.addEventListener('add-dashboard-to-overview', handleAddDashboardEvent as EventListener);
    return () => {
      window.removeEventListener('navigate-to-view', handleNavigate as EventListener);
      window.removeEventListener('add-dashboard-to-overview', handleAddDashboardEvent as EventListener);
    };
  }, [setActiveView]);
  // Scroll to top when switching dashboards
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activeView]);
  // Smart layout logic - pair small and large cards in rows
  const renderCards = () => {
    const rows: JSX.Element[] = [];
    let i = 0;
    while (i < visibleCards.length) {
      const currentCard = visibleCards[i];
      const nextCard = visibleCards[i + 1];
      // Check if we can pair a small and large card
      if (currentCard && nextCard) {
        const hasSmallAndLarge = currentCard.size === 'small' && nextCard.size === 'large' || currentCard.size === 'large' && nextCard.size === 'small';
        if (hasSmallAndLarge) {
          // Pair small and large in one row - give more space to small card (models panel)
          const smallCard = currentCard.size === 'small' ? currentCard : nextCard;
          const largeCard = currentCard.size === 'large' ? currentCard : nextCard;
          rows.push(<motion.div key={`row-${i}`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[450px]" data-id="element-152">
              <div className="lg:col-span-5 h-full" data-id="element-153">
                {renderCard(smallCard, true)}
              </div>
              <div className="lg:col-span-7 h-full" data-id="element-154">
                {renderCard(largeCard, true)}
              </div>
            </motion.div>);
          i += 2;
          continue;
        }
      }
      // Handle two small cards in a row
      if (currentCard?.size === 'small' && nextCard?.size === 'small') {
        rows.push(<motion.div key={`row-${i}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[400px]" data-id="element-155">
            {renderCard(currentCard, true)}
            {renderCard(nextCard, true)}
          </motion.div>);
        i += 2;
        continue;
      }
      // Handle two large cards (each on own row)
      if (currentCard?.size === 'large' && nextCard?.size === 'large') {
        rows.push(<motion.div key={`row-${i}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="min-h-[450px]" data-id="element-156">
            {renderCard(currentCard, true)}
          </motion.div>);
        i += 1;
        continue;
      }
      // Single card (last one)
      if (currentCard) {
        rows.push(<motion.div key={`row-${i}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className={currentCard.size === 'small' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[400px]' : 'min-h-[450px]'} data-id="element-157">
            {renderCard(currentCard, true)}
          </motion.div>);
        i += 1;
      }
    }
    return rows;
  };
  const renderCard = (card: DashboardCard, fillHeight: boolean = false) => {
    const cardComponents = {
      models: <ModelsPanel data-id="element-158" />,
      zones: <MarketZonesPanel data-id="element-159" />,
      charts: <ChartsPanel activeRegion={activeRegion} activeZone={activeZone} activeVariable={activeVariable} data-id="element-160" />,
      heatmap: <HeatmapPanel data-id="element-161" />
    };
    return <motion.div key={card.id} layout initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} transition={{
      duration: 0.3
    }} className={fillHeight ? 'h-full' : card.size === 'small' ? 'h-[400px]' : 'min-h-[450px]'} data-id="element-162">
        {cardComponents[card.id as keyof typeof cardComponents]}
      </motion.div>;
  };
  // NEW: Track if any modal is open to hide nav bars
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  return <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 text-slate-800 font-sans selection:bg-[#ff006b]/20 relative overflow-hidden" data-id="element-163">
      {/* Animated gradient orbs */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-pink-300/30 via-purple-300/30 to-transparent rounded-full blur-3xl animate-pulse" data-id="element-164" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-300/20 via-purple-300/20 to-transparent rounded-full blur-3xl animate-pulse delay-1000" data-id="element-165" />

      {/* Sidebar - conditionally rendered with animation */}
      <AnimatePresence data-id="element-166">
        {!isAnyModalOpen && <motion.div initial={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.2
      }} data-id="element-167">
            <Sidebar collapsed={isSidebarCollapsed} toggleCollapsed={toggleSidebar} activeView={activeView} setActiveView={setActiveView} data-id="element-168" />
          </motion.div>}
      </AnimatePresence>

      <div className={`flex flex-col min-h-screen transition-all duration-300 ${isAnyModalOpen ? 'ml-0' : isSidebarCollapsed ? 'ml-20' : 'ml-[260px]'}`} data-id="element-169">
        {/* TopBar - conditionally rendered with animation */}
        <AnimatePresence data-id="element-170">
          {!isAnyModalOpen && <motion.div initial={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-171">
              <TopBar activeRegion={activeRegion} setActiveRegion={setActiveRegion} searchQuery={searchQuery} setSearchQuery={setSearchQuery} activeView={activeView} setActiveView={setActiveView} notifications={notifications} hasUnreadNotifications={hasUnreadNotifications} onMarkAllAsRead={markAllNotificationsAsRead} onMarkAsRead={markNotificationAsRead} readNotifications={readNotifications} availableRegions={availableRegions} onNewChat={clearChatHistory} data-id="element-172" />
            </motion.div>}
        </AnimatePresence>

        <main className="flex-1 p-6 relative z-10" data-id="element-173">
          <div className="max-w-[1600px] mx-auto h-full flex flex-col overflow-visible" data-id="element-174">
            {/* Render different views based on activeView */}
            {activeView === 'overview' && <OverviewView activeRegion={activeRegion} setActiveRegion={setActiveRegion} onNotificationsChange={setNotifications} onModalOpenChange={setIsAnyModalOpen} data-id="element-175" />}

            {activeView === 'briefing' && <BriefingView data-id="element-176" />}

            {/* Oracle View - Kept for full screen experience, but widget is also available */}
            {activeView === 'oracle' && <OracleView messages={chatMessages} sendMessage={sendChatMessage} isLoading={isChatLoading} suggestedFollowUps={chatFollowUps} clearHistory={clearChatHistory} loadMessages={loadChatMessages} onModalOpenChange={setIsAnyModalOpen} data-id="element-177" />}

            {activeView === 'forecast' && <ForecastView activeRegion={activeRegion} activeZone={activeZone} data-id="element-178" />}

            {activeView === 'benchmarks' && <BenchmarksView activeRegion={activeRegion} activeZone={activeZone} data-id="element-179" />}

            {activeView === 'trading-strategies' && <TradingStrategiesView data-id="element-180" />}

            {activeView === 'dashboards' && <DashboardsView dashboards={visibleDashboards} allAvailableDashboards={allAvailableDashboards} setSelectedDashboardId={handleSelectDashboard} onAddDashboard={handleAddDashboard} onRemoveDashboard={handleRemoveDashboard} onCreateDashboard={handleCreateDashboard} onModalOpenChange={setIsAnyModalOpen} data-id="element-181" />}

            {/* Region-specific dashboards - each with unique layout and focus */}
            {activeView === 'germany-pricing' && <GermanyPricingDashboard data-id="element-182" />}
            {activeView === 'nordics-weather' && <NordicsWeatherDashboard data-id="element-183" />}
            {activeView === 'spain-solar' && <SpainSolarDashboard data-id="element-184" />}
            {activeView === 'uk-trading' && <UKTradingDashboard data-id="element-185" />}
            {activeView === 'france-market' && <FranceMarketDashboard data-id="element-186" />}

            {activeView === 'europe-pricing' && <EuropePricingDashboard data-id="element-187" />}

            {/* Market Intelligence Dashboards */}
            {activeView === 'cross-border-flow' && <CrossBorderFlowDashboard data-id="element-188" />}
            {activeView === 'renewable-correlation' && <RenewableCorrelationDashboard data-id="element-189" />}
            {activeView === 'intraday-liquidity' && <IntradayLiquidityDashboard data-id="element-190" />}

            {/* Risk & Portfolio Dashboards */}
            {activeView === 'position-risk' && <PositionRiskDashboard data-id="element-191" />}
            {activeView === 'weather-impact' && <WeatherImpactDashboard data-id="element-192" />}
            {activeView === 'balancing-market' && <BalancingMarketDashboard data-id="element-193" />}

            {/* Operational Dashboards */}
            {activeView === 'asset-performance' && <AssetPerformanceDashboard data-id="element-194" />}
            {activeView === 'congestion-analysis' && <CongestionAnalysisDashboard data-id="element-195" />}

            {/* Advanced Analytics Dashboards */}
            {activeView === 'ml-model-performance' && <MLModelPerformanceDashboard data-id="element-196" />}
            {activeView === 'scenario-planning' && <ScenarioPlanningDashboard data-id="element-197" />}

            {/* Render custom dashboards */}
            {visibleDashboards.find(d => d.id === activeView && d.config) && (() => {
            const dashboard = visibleDashboards.find(d => d.id === activeView);
            if (!dashboard?.config) return null;
            // Route to appropriate template component
            if (dashboard.config.templateId === 'europe-overview' || dashboard.config.templateId === 'region-pricing') {
              return <EuropePricingDashboard data-id="element-198" />;
            }
            // Add more template routing here as we create more templates
            return null;
          })()}

            {activeView === 'market-aggregates' && <div className="flex flex-col h-full gap-6 overflow-visible" data-id="element-199">
                {/* Header with FilterBar and Cards button */}
                <div className="flex items-center justify-between gap-4 overflow-visible" data-id="element-200">
                  <div className="flex-1 overflow-visible" data-id="element-201">
                    <FilterBar activeFilters={activeFilters} toggleFilter={toggleFilter} activeZone={activeZone} setActiveZone={setActiveZone} activeVariable={activeVariable} setActiveVariable={setActiveVariable} data-id="element-202" />
                  </div>
                  <button onClick={() => {
                setIsCardPanelOpen(!isCardPanelOpen);
                setIsAnyModalOpen(!isCardPanelOpen);
              }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white bg-white/60 hover:bg-white/80 transition-colors text-xs font-medium text-slate-700 shadow-sm backdrop-blur-sm" data-id="element-203">
                    <span className="w-2 h-2 rounded-full bg-[#ff006b] animate-pulse shadow-sm shadow-pink-500/50" data-id="element-204" />
                    Cards ({visibleCount})
                  </button>
                </div>

                {/* Dynamic Card Layout */}
                <AnimatePresence mode="popLayout" data-id="element-205">
                  <div className="flex flex-col gap-6 flex-1 overflow-visible" data-id="element-206">
                    {renderCards()}
                  </div>
                </AnimatePresence>

                {/* Card Customization Panel */}
                <CardCustomizationPanel isOpen={isCardPanelOpen} onClose={() => {
              setIsCardPanelOpen(false);
              setIsAnyModalOpen(false);
            }} cards={cards} onCardsChange={setCards} data-id="element-207" />
              </div>}

            {/* Placeholder for other views */}
            {!['overview', 'briefing', 'oracle', 'forecast', 'benchmarks', 'trading-strategies', 'dashboards', 'market-aggregates'].includes(activeView) && !visibleDashboards.find(d => d.id === activeView) && <div className="flex items-center justify-center min-h-[400px]" data-id="element-208">
                  <div className="text-center" data-id="element-209">
                    <p className="text-slate-500 text-sm" data-id="element-210">
                      Dashboard view not found
                    </p>
                  </div>
                </div>}
          </div>
        </main>
      </div>

      {/* Oracle Chat Widget - Always available except on full Oracle view */}
      {activeView !== 'oracle' && <OracleChatWidget messages={chatMessages} sendMessage={sendChatMessage} isLoading={isChatLoading} onClearHistory={clearChatHistory} data-id="element-211" />}

      {/* Fallback button to show navbars - appears when hidden */}
      <AnimatePresence data-id="element-212">
        {isAnyModalOpen && <motion.button initial={{
        opacity: 0,
        scale: 0.8,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8,
        y: 20
      }} onClick={() => setIsAnyModalOpen(false)} className="fixed bottom-6 right-6 z-50 bg-pink-500 hover:bg-pink-600 text-white p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2 group" title="Show navigation" data-id="element-213">
            <Menu className="w-5 h-5" data-id="element-214" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap text-sm font-medium" data-id="element-215">
              Show Navigation
            </span>
          </motion.button>}
      </AnimatePresence>
    </div>;
}